import { lazy, Suspense, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  Apple,
  Calendar,
  CreditCard,
  HardDrive,
  Hash,
  MapPin,
  MessageCircle,
  TrendingUp,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { LoadingState } from "@/components/data/LoadingState";
import { ErrorState } from "@/components/data/ErrorState";
import { EmptyState } from "@/components/data/EmptyState";
import { DataTable, type Column } from "@/components/data/DataTable";
import { JsonViewer } from "@/components/data/JsonViewer";
import { KpiCard } from "@/components/data/KpiCard";
import { SparkLine } from "@/components/charts/SparkLine";
import { RelTime } from "@/components/data/RelTime";
import { StatusBadge } from "@/components/data/StatusBadge";
import { UserAvatar } from "@/components/user/UserAvatar";
import { RoleBadge, StatusPill } from "@/components/user/UserBadges";
import { UserActions } from "@/components/user/UserActions";
import { LocationBadge, LocationFacts, LocationMap, formatLocationLabel } from "@/components/location/LocationDisplay";
import { userMapPointFromLocation } from "@/components/location/UserMapData";
import {
  useUserAccounts,
  useUserActivity,
  useUserBudgets,
  useUserCorrections,
  useUserDevices,
  useUserErrors,
  useUserExpenses,
  useUserFull,
  useUserGoals,
  useUserIncome,
  useMessages,
  useUserNotifications,
  useUserRecurring,
  useUserSavings,
  useUserSessions,
  useUserTrips,
  paginatedItems,
} from "@/lib/queries";
import { API_BASE } from "@/lib/api";
import { authClient } from "@/lib/auth";
import { fmtCurrency, fmtDate, fmtNumber, truncate } from "@/lib/format";
import { isEffectivePro, isStalePro } from "@/lib/subscription";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import type {
  ActivityRow,
  BudgetRow,
  CorrectionPrevious,
  CorrectionRow,
  DeviceRow,
  ErrorRow,
  ExpenseRow,
  GoalRow,
  IncomeRow,
  LocationCountryBucket,
  LocationSnapshot,
  MessageRow,
  NotificationRow,
  RecurringRow,
  SavingsRow,
  SessionRow,
  TripRow,
  UserFullResponse,
} from "@/types";
import { cn } from "@/lib/utils";

const PAGE = 50;
const LazyUserMap = lazy(async () => {
  const mod = await import("@/components/location/UserMap");
  return { default: mod.UserMap };
});

function profilePhotoUploadId(image?: string | null): string | null {
  if (!image) return null;
  try {
    const path = new URL(image, window.location.origin).pathname;
    const match = path.match(/\/api\/profile-photos\/([^/]+)\.(?:jpe?g|png|webp)$/i);
    return match?.[1] ?? null;
  } catch {
    return null;
  }
}

function profilePhotoExtension(image?: string | null): string {
  if (!image) return "jpg";
  try {
    const path = new URL(image, window.location.origin).pathname;
    const match = path.match(/\/api\/profile-photos\/[^/]+\.(jpe?g|png|webp)$/i);
    const ext = match?.[1]?.toLowerCase();
    return ext === "jpeg" ? "jpg" : ext ?? "jpg";
  } catch {
    return "jpg";
  }
}

function originalProfilePhotoURL(userId: string, image?: string | null): string | null {
  const uploadId = profilePhotoUploadId(image);
  if (!uploadId) return null;
  const ext = profilePhotoExtension(image);
  return `${API_BASE}/api/admin/users/${encodeURIComponent(userId)}/profile-photo/original/${encodeURIComponent(uploadId)}.${ext}`;
}

export function UserDetailPage() {
  const { id = "" } = useParams();
  const full = useUserFull(id);
  const [photoOpen, setPhotoOpen] = useState(false);
  useDocumentTitle(full.data?.user.email ?? "User");

  if (full.isLoading) return <LoadingState label="Loading user…" />;
  if (full.isError) return <ErrorState error={full.error} onRetry={() => full.refetch()} />;
  if (!full.data) return null;

  const { user, config, counts, totals, by_category, by_currency, recent_activity_30d, active_streak_days, integrations, subscription } = full.data;
  const userLocation = full.data.location ?? { last: null, last_precise: null, recent: [], countries_30d: [] };
  const primaryLocation = userLocation.last_precise ?? userLocation.last;
  const originalPhotoURL = originalProfilePhotoURL(user.id, user.image);

  return (
    <>
      <div className="mb-4 flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/users">
            <ArrowLeft className="h-4 w-4" /> Users
          </Link>
        </Button>
        <Separator orientation="vertical" className="h-5" />
        <span className="font-mono text-xs text-muted-foreground">{user.id}</span>
      </div>

      <Card className="mb-6 p-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-4">
            {originalPhotoURL ? (
              <button
                type="button"
                onClick={() => setPhotoOpen(true)}
                className="shrink-0 rounded-full focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
                aria-label="View full-resolution profile photo"
              >
                <UserAvatar
                  size="xl"
                  name={user.name}
                  email={user.email}
                  image={user.image}
                  className="cursor-zoom-in transition-shadow hover:ring-2 hover:ring-sky-300/70"
                />
              </button>
            ) : (
              <UserAvatar size="xl" name={user.name} email={user.email} image={user.image} />
            )}
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="truncate text-2xl font-semibold tracking-tight">{user.name ?? user.email}</h1>
                <RoleBadge role={user.role} />
                <StatusPill user={user} />
              </div>
              <div className="mt-1 font-mono text-sm text-muted-foreground">{user.email}</div>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> Joined {fmtDate(user.createdAt)}
                </span>
                {user.last_active_at && (
                  <span className="inline-flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" /> Active <RelTime value={user.last_active_at} />
                  </span>
                )}
                <span className="inline-flex items-center gap-1">
                  <Hash className="h-3 w-3" /> {fmtStreak(active_streak_days)} streak
                </span>
                {primaryLocation ? (
                  <span className="inline-flex min-w-0 items-center gap-1">
                    <MapPin className="h-3 w-3 shrink-0" />
                    <LocationBadge location={primaryLocation} showTime />
                  </span>
                ) : null}
              </div>
              {user.banned ? (
                <div className="mt-3 inline-flex items-center gap-2 rounded-md border border-rose-500/30 bg-rose-500/10 px-2.5 py-1 text-xs text-rose-300">
                  <strong>Banned:</strong>
                  <span>{user.banReason ?? "no reason"}</span>
                  {user.banExpires && (
                    <span className="text-rose-400/80">
                      until {fmtDate(user.banExpires)}
                    </span>
                  )}
                </div>
              ) : null}
            </div>
          </div>
          <div className="flex flex-col items-stretch gap-3">
            <UserActions user={user} variant="buttons" />
          </div>
        </div>
      </Card>

      <FullResPhotoDialog
        open={photoOpen}
        onOpenChange={setPhotoOpen}
        url={originalPhotoURL}
        alt={`${user.name ?? user.email} profile photo`}
      />

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="flex w-full flex-wrap">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="expenses">Expenses</TabsTrigger>
          <TabsTrigger value="messages">Messages</TabsTrigger>
          <TabsTrigger value="errors">Errors</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="devices">Devices &amp; Sessions</TabsTrigger>
          <TabsTrigger value="data">Settings/Data</TabsTrigger>
          <TabsTrigger value="danger" className="text-rose-300 data-[state=active]:text-rose-200">Danger</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <section className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
            <KpiCard label="Expenses" value={fmtNumber(counts.expenses)} icon={<CreditCard className="h-4 w-4" />} />
            <KpiCard
              label={totals.currency ? `Spent (${totals.currency})` : "Spent"}
              value={totals.currency ? fmtCurrency(totals.expense_sum, totals.currency, { compact: true }) : "—"}
              hint={totals.has_other_currencies ? "other currencies: see By currency" : undefined}
              accent="info"
            />
            <KpiCard
              label={totals.currency ? `Income (${totals.currency})` : "Income"}
              value={totals.currency ? fmtCurrency(totals.income_sum, totals.currency, { compact: true }) : "—"}
              accent="success"
            />
            <KpiCard label="Streak" value={fmtStreak(active_streak_days)} hint={`${full.data.active_days_30d}/7 days active`} />
            <KpiCard label="Devices" value={fmtNumber(counts.devices)} icon={<HardDrive className="h-4 w-4" />} />
            <KpiCard label="Errors 7d" value={fmtNumber(counts.errors_7d)} accent={counts.errors_7d > 0 ? "danger" : "default"} />
          </section>

          <UserLocationSection location={userLocation} config={config} user={user} />

          <section className="grid gap-4 lg:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle>Activity (7d)</CardTitle>
              </CardHeader>
              <CardContent>
                <SparkLine data={recent_activity_30d} color="#38bdf8" height={120} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>By category</CardTitle>
              </CardHeader>
              <CardContent>
                {by_category.length === 0 ? (
                  <EmptyState />
                ) : (
                  <ul className="space-y-1.5 text-sm">
                    {by_category.slice(0, 8).map((c) => {
                      // Bars compare entry counts: sums in different currencies aren't comparable.
                      const max = Math.max(...by_category.map((x) => x.count));
                      const pct = max ? (c.count / max) * 100 : 0;
                      return (
                        <li key={`${c.category}-${c.currency}`} className="space-y-0.5">
                          <div className="flex items-center justify-between text-xs">
                            <span className="truncate">{c.category || "uncategorized"}</span>
                            <span className="tabular-nums text-muted-foreground">
                              {c.count} · {fmtCurrency(c.sum, c.currency, { compact: true })}
                            </span>
                          </div>
                          <div className="h-1 w-full overflow-hidden rounded bg-secondary">
                            <div className="h-full bg-emerald-400/70" style={{ width: `${pct}%` }} />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>By currency</CardTitle>
              </CardHeader>
              <CardContent>
                {by_currency.length === 0 ? (
                  <EmptyState />
                ) : (
                  <ul className="space-y-1.5 text-sm">
                    {by_currency.map((c) => (
                      <li key={c.currency} className="flex items-center justify-between text-xs">
                        <span className="font-mono">{c.currency}</span>
                        <span className="tabular-nums text-muted-foreground">
                          {c.count} · spent {fmtCurrency(c.expense_sum, c.currency, { compact: true })}
                          {c.income_sum ? ` · income ${fmtCurrency(c.income_sum, c.currency, { compact: true })}` : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </section>

          <section>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Subscription</CardTitle>
                <Link
                  to={`/subscriptions/${user.id}`}
                  className="text-xs text-sky-300 hover:underline"
                >
                  Open full view →
                </Link>
              </CardHeader>
              <CardContent>
                {subscription ? (
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
                    <div className="flex items-center gap-2">
                      <Badge variant={
                        isStalePro(subscription) ? "error" :
                        isEffectivePro(subscription) && subscription.is_in_grace_period ? "warn" :
                        isEffectivePro(subscription) && (subscription.billing_issues_detected || subscription.unsubscribe_detected) ? "warn" :
                        isEffectivePro(subscription) ? "success" : "error"
                      }>
                        {isStalePro(subscription) ? "Expired (stale)" :
                          isEffectivePro(subscription) && subscription.is_in_grace_period ? "Grace" :
                          isEffectivePro(subscription) && subscription.billing_issues_detected ? "Billing issue" :
                          isEffectivePro(subscription) && subscription.unsubscribe_detected ? "Will not renew" :
                          isEffectivePro(subscription) ? "Active" : "Inactive"}
                      </Badge>
                      {subscription.is_sandbox ? <Badge variant="warn" className="font-mono text-[10px]">SANDBOX</Badge> : null}
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Entitlement</span>
                      <span className="font-mono text-xs">{subscription.entitlement_id ?? "—"}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Product</span>
                      <span className="font-mono text-xs">{subscription.product_id ?? "—"}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Store</span>
                      <span className="text-xs">{subscription.store ?? "—"}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Expires</span>
                      <span className="text-xs">
                        {subscription.expires_at_ms ? fmtDate(new Date(subscription.expires_at_ms).toISOString()) : "—"}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Latest event</span>
                      <span className="font-mono text-[11px] text-muted-foreground">{subscription.latest_event_type ?? "—"}</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">
                    No RevenueCat data yet. Will populate when the user purchases or after iOS calls{" "}
                    <code className="font-mono text-xs">Purchases.logIn(user.id)</code>.
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          <section>
            <Card>
              <CardHeader>
                <CardTitle>Counts</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
                  {Object.entries(counts).map(([k, v]) => (
                    <Stat key={k} label={k.replaceAll("_", " ")} value={v} />
                  ))}
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  Integrations:
                  <Badge variant={integrations.ios ? "info" : "outline"} className="gap-1">
                    <Apple className="h-3 w-3" /> iOS {integrations.ios ? "linked" : "—"}
                  </Badge>
                  <Badge variant={integrations.telegram ? "info" : "outline"} className="gap-1">
                    Telegram {integrations.telegram ? "linked" : "—"}
                  </Badge>
                  <Badge variant={integrations.whatsapp ? "info" : "outline"} className="gap-1">
                    <MessageCircle className="h-3 w-3" /> WhatsApp {integrations.whatsapp ? "linked" : "—"}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </section>
        </TabsContent>

        <TabsContent value="expenses">
          <ExpensesTab id={id} />
        </TabsContent>
        <TabsContent value="messages">
          <MessagesTab id={id} />
        </TabsContent>
        <TabsContent value="errors">
          <ErrorsTab id={id} />
        </TabsContent>
        <TabsContent value="activity">
          <ActivityTab id={id} />
        </TabsContent>
        <TabsContent value="devices">
          <DevicesTab id={id} />
        </TabsContent>
        <TabsContent value="data">
          <DataTab id={id} config={config} />
        </TabsContent>
        <TabsContent value="danger">
          <DangerTab user={user} />
        </TabsContent>
      </Tabs>
    </>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-border/40 bg-secondary/30 px-3 py-2">
      <div className="text-base font-semibold tabular-nums">{fmtNumber(value)}</div>
      <div className="mt-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    </div>
  );
}

function FullResPhotoDialog({
  open,
  onOpenChange,
  url,
  alt,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  url: string | null;
  alt: string;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");

  useEffect(() => {
    if (!open || !url) {
      setPreviewUrl(null);
      setStatus("idle");
      return;
    }

    const controller = new AbortController();
    let objectUrl: string | null = null;
    setPreviewUrl(null);
    setStatus("loading");

    fetch(url, {
      credentials: "include",
      headers: { Accept: "image/*" },
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        return res.blob();
      })
      .then((blob) => {
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
        setStatus("ready");
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error("Full-resolution profile photo preview failed:", err);
        setStatus("error");
      });

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, url]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-hidden p-0">
        <DialogTitle className="sr-only">Full-resolution profile photo</DialogTitle>
        <div className="grid min-h-[260px] place-items-center bg-black/80">
          {status === "loading" ? (
            <div className="p-8">
              <LoadingState label="Loading photo…" />
            </div>
          ) : status === "error" ? (
            <div className="p-8 text-center">
              <div className="text-sm font-medium text-foreground">This photo could not be previewed.</div>
              <div className="mt-1 text-xs text-muted-foreground">The original may be missing or in a browser-unsupported format.</div>
            </div>
          ) : previewUrl ? (
            <img src={previewUrl} alt={alt} className="max-h-[88vh] w-full object-contain" />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UserLocationSection({
  location,
  config,
  user,
}: {
  location: {
    last: LocationSnapshot | null;
    last_precise: LocationSnapshot | null;
    recent: LocationSnapshot[];
    countries_30d: LocationCountryBucket[];
  };
  config: Record<string, unknown> | null;
  user: UserFullResponse["user"];
}) {
  const primary = location.last_precise ?? location.last;
  const configuredTimezone = typeof config?.timezone === "string" ? config.timezone : null;
  const mapUser = userMapPointFromLocation({ user, location: primary });

  return (
    <section className="grid gap-4 xl:grid-cols-[minmax(0,2fr),minmax(320px,1fr)]">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle>User map</CardTitle>
            <LocationBadge location={primary} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {mapUser ? (
            <Suspense
              fallback={
                <div className="grid h-[320px] place-items-center rounded-md border border-border/60 bg-secondary/30">
                  <LoadingState label="Loading map…" />
                </div>
              }
            >
              <LazyUserMap users={[mapUser]} heightClassName="h-[320px]" selectedUserId={user.id} />
            </Suspense>
          ) : (
            <LocationMap location={primary} heightClassName="h-[320px]" />
          )}
          <LocationFacts location={primary} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Location context</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Stat label="precise points" value={location.recent.filter((r) => r.latitude != null && r.longitude != null).length} />
            <Stat label="countries 7d" value={location.countries_30d.length} />
          </div>

          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Operating context</div>
            <div className="rounded-md border border-border/40 bg-secondary/30 px-3 py-2 text-xs">
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Latest</span>
                <span className="truncate text-right">{formatLocationLabel(location.last)}</span>
              </div>
              {configuredTimezone ? (
                <div className="mt-1 flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Config timezone</span>
                  <span className="truncate font-mono text-right">{configuredTimezone}</span>
                </div>
              ) : null}
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Countries seen</div>
            {location.countries_30d.length === 0 ? (
              <div className="text-xs text-muted-foreground">No country signals in the last 7 days.</div>
            ) : (
              <ul className="space-y-1.5 text-xs">
                {location.countries_30d.map((c) => (
                  <li key={c.country} className="flex items-center justify-between gap-3">
                    <span className="font-mono">{c.country}</span>
                    <span className="tabular-nums text-muted-foreground">{fmtNumber(c.count)} requests</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Recent points</div>
            {location.recent.length === 0 ? (
              <div className="text-xs text-muted-foreground">No location-bearing requests yet.</div>
            ) : (
              <ul className="space-y-2">
                {location.recent.slice(0, 5).map((loc) => (
                  <li key={loc.id ?? `${loc.path}-${loc.observed_at}`} className="min-w-0 rounded-md border border-border/40 bg-secondary/20 px-3 py-2">
                    <div className="flex min-w-0 items-center justify-between gap-2">
                      <LocationBadge location={loc} showTime />
                    </div>
                    {loc.path ? <div className="mt-1 truncate font-mono text-[11px] text-muted-foreground">{loc.path}</div> : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

// ---------------- Expenses ----------------
function ExpensesTab({ id }: { id: string }) {
  const [page, setPage] = useState(0);
  const q = useUserExpenses(id, { limit: PAGE, offset: page * PAGE });
  const rows = paginatedItems<ExpenseRow>(q.data);
  const total = q.data?.total ?? rows.length;

  const cols: Column<ExpenseRow>[] = [
    { key: "date", header: "Date", cell: (r) => <span className="font-mono text-xs">{r.expense_date}</span>, width: 110 },
    { key: "type", header: "Type", cell: (r) => <Badge variant="outline">{r.type ?? "—"}</Badge>, width: 90 },
    { key: "category", header: "Category", cell: (r) => r.category ?? "—" },
    { key: "note", header: "Note", cell: (r) => <span className="text-xs text-muted-foreground">{truncate(r.note ?? "", 80)}</span> },
    {
      key: "amount",
      header: "Amount",
      cell: (r) => <span className="tabular-nums">{fmtCurrency(r.amount, r.currency)}</span>,
      className: "text-right",
      width: 130,
    },
    {
      key: "source",
      header: "Source",
      cell: (r) => <Badge variant="neutral" className="font-mono text-[10px] uppercase">{r.source ?? "text"}</Badge>,
      width: 90,
    },
  ];

  return (
    <DataTable
      columns={cols}
      rows={rows}
      loading={q.isLoading}
      error={q.isError ? q.error : undefined}
      onRetry={() => q.refetch()}
      rowKey={(r) => r.id}
      page={page}
      onPageChange={setPage}
      pageSize={PAGE}
      totalRows={total}
      expandable={(r) => (
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <div className="mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">raw_text</div>
            <div className="rounded-md border border-border/40 bg-background/40 p-2 font-mono text-xs">
              {r.raw_text || <span className="text-muted-foreground">—</span>}
            </div>
          </div>
          <div>
            <div className="mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">details</div>
            <JsonViewer
              value={{
                id: r.id,
                created_at: r.created_at,
                amount: r.amount,
                currency: r.currency,
                original_amount: r.original_amount,
                original_currency: r.original_currency,
                exchange_rate: r.exchange_rate,
                receipt_key: r.receipt_key,
                corrections: r.corrections_count,
              }}
            />
          </div>
        </div>
      )}
    />
  );
}

// ---------------- Messages ----------------
function MessagesTab({ id }: { id: string }) {
  const [page, setPage] = useState(0);
  const [channel, setChannel] = useState<"all" | "telegram" | "whatsapp">("all");
  // The global /messages listing filters by user and channel server-side.
  const q = useMessages({ user_id: id, limit: PAGE, offset: page * PAGE, channel: channel === "all" ? undefined : channel });
  const filtered = paginatedItems<MessageRow>(q.data);
  const total = q.data?.total ?? filtered.length;
  const [selected, setSelected] = useState<MessageRow | null>(null);
  const sel = (selected && filtered.some((m) => m.id === selected.id) ? selected : null) ?? filtered[0] ?? null;

  return (
    <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr),minmax(0,3fr)]">
      <Card className="overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle>Messages</CardTitle>
          <div className="flex gap-1" role="group" aria-label="Filter messages by channel">
            {(["all", "telegram", "whatsapp"] as const).map((c) => (
              <button
                key={c}
                type="button"
                aria-pressed={channel === c}
                onClick={() => {
                  setChannel(c);
                  setPage(0);
                  setSelected(null);
                }}
                className={cn(
                  "rounded-full px-2.5 py-0.5 text-[11px] capitalize transition-colors",
                  channel === c ? "bg-foreground text-background" : "bg-secondary/60 text-muted-foreground hover:text-foreground"
                )}
              >
                {c}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="space-y-1 p-0">
          <div className="max-h-[600px] overflow-y-auto scrollbar-thin">
            {q.isLoading ? (
              <LoadingState />
            ) : q.isError && filtered.length === 0 ? (
              <ErrorState error={q.error} onRetry={() => q.refetch()} />
            ) : filtered.length === 0 ? (
              <EmptyState title="No messages" />
            ) : (
              <ul className="divide-y divide-border/40">
                {filtered.map((m) => (
                  <li key={String(m.id)}>
                    <button
                      onClick={() => setSelected(m)}
                      className={cn(
                        "w-full px-4 py-2.5 text-left transition-colors hover:bg-secondary/40",
                        sel?.id === m.id && "bg-secondary/60"
                      )}
                    >
                      <div className="flex items-center gap-2 text-[11px]">
                        <Badge variant={m.channel === "telegram" ? "info" : "success"} className="capitalize">
                          {m.channel}
                        </Badge>
                        <Badge variant="outline" className="capitalize">{m.direction}</Badge>
                        <span className="ml-auto text-muted-foreground">
                          <RelTime value={m.created_at} />
                        </span>
                      </div>
                      <div className="mt-1 line-clamp-2 text-xs">{m.text || <span className="text-muted-foreground">(no text)</span>}</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex items-center justify-between gap-2 border-t border-border/40 px-3 py-2 text-xs text-muted-foreground">
            <span className="tabular-nums">
              {total === 0 ? "0 messages" : `${page * PAGE + 1}–${Math.min((page + 1) * PAGE, total)} of ${total.toLocaleString()}`}
            </span>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                Prev
              </Button>
              <Button size="sm" variant="ghost" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Detail</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!sel ? (
            <EmptyState title="Select a message" />
          ) : (
            <>
              <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Inbound</div>
                <div className="rounded-md border border-border/50 bg-background/40 p-3 font-mono text-xs whitespace-pre-wrap">
                  {sel.text || <span className="text-muted-foreground">—</span>}
                </div>
              </div>
              {sel.ai_response && (
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">AI response</div>
                  <div className="rounded-md border border-sky-500/20 bg-sky-500/5 p-3 font-mono text-xs whitespace-pre-wrap">
                    {sel.ai_response}
                  </div>
                </div>
              )}
              {sel.expense_id && (
                <div>
                  <Badge variant="info">expense #{sel.expense_id}</Badge>
                </div>
              )}
              {sel.error && (
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-rose-300">Error</div>
                  <div className="rounded-md border border-rose-500/30 bg-rose-500/10 p-3 font-mono text-xs text-rose-200">
                    {sel.error}
                  </div>
                </div>
              )}
              {sel.raw != null && (
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Raw</div>
                  <JsonViewer value={sel.raw} />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------- Errors ----------------
function ErrorsTab({ id }: { id: string }) {
  const [page, setPage] = useState(0);
  const q = useUserErrors(id, { limit: PAGE, offset: page * PAGE });
  const rows = paginatedItems<ErrorRow>(q.data);
  const total = q.data?.total ?? rows.length;

  const cols: Column<ErrorRow>[] = [
    { key: "time", header: "Time", cell: (r) => <span className="text-xs text-muted-foreground"><RelTime value={r.created_at} /></span>, width: 120 },
    { key: "status", header: "Status", cell: (r) => <StatusBadge status={r.status} />, width: 80 },
    { key: "method", header: "Method", cell: (r) => <span className="font-mono text-xs">{r.method ?? "—"}</span>, width: 70 },
    { key: "path", header: "Path", cell: (r) => <span className="font-mono text-xs">{r.path ?? "—"}</span> },
    { key: "message", header: "Message", cell: (r) => <span className="text-xs text-rose-200/80">{truncate(r.message ?? "", 100)}</span> },
  ];
  return (
    <DataTable
      columns={cols}
      rows={rows}
      loading={q.isLoading}
      error={q.isError ? q.error : undefined}
      onRetry={() => q.refetch()}
      rowKey={(r) => r.id}
      page={page}
      onPageChange={setPage}
      pageSize={PAGE}
      totalRows={total}
      expandable={(r) => (
        <pre className="overflow-x-auto whitespace-pre-wrap rounded-md border border-border/40 bg-background/40 p-3 font-mono text-[11px] leading-relaxed text-muted-foreground">
{r.stack ?? r.message ?? "no stack"}
        </pre>
      )}
    />
  );
}

// ---------------- Activity ----------------
function ActivityTab({ id }: { id: string }) {
  const [page, setPage] = useState(0);
  const q = useUserActivity(id, { limit: PAGE, offset: page * PAGE });
  const rows = paginatedItems<ActivityRow>(q.data);
  const total = q.data?.total ?? rows.length;

  const cols: Column<ActivityRow>[] = [
    { key: "time", header: "Time", cell: (r) => <span className="text-xs text-muted-foreground"><RelTime value={r.created_at} /></span>, width: 120 },
    { key: "method", header: "Method", cell: (r) => <span className="font-mono text-xs">{r.method ?? "—"}</span>, width: 70 },
    { key: "path", header: "Path", cell: (r) => <span className="font-mono text-xs">{r.path ?? "—"}</span> },
    { key: "status", header: "Status", cell: (r) => <StatusBadge status={r.status} />, width: 80 },
    { key: "duration", header: "Duration", cell: (r) => <span className="font-mono text-xs tabular-nums">{r.duration_ms ?? 0} ms</span>, width: 100 },
    { key: "location", header: "Location", cell: (r) => <LocationBadge location={r} />, width: 180 },
    { key: "ua", header: "User-Agent", cell: (r) => <span className="font-mono text-xs text-muted-foreground">{truncate(r.user_agent ?? "", 60)}</span> },
  ];
  return (
    <DataTable
      columns={cols}
      rows={rows}
      loading={q.isLoading}
      error={q.isError ? q.error : undefined}
      onRetry={() => q.refetch()}
      rowKey={(r) => r.id}
      page={page}
      onPageChange={setPage}
      pageSize={PAGE}
      totalRows={total}
      expandable={(r) => (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr),minmax(0,1fr)]">
          <LocationMap location={r} heightClassName="h-[220px]" />
          <div className="space-y-3">
            <LocationFacts location={r} />
            <JsonViewer
              value={{
                request_id: r.request_id ?? r.id,
                ip: r.ip,
                country: r.country,
                location_country: r.location_country,
                timezone: r.timezone,
                city: r.city,
                region: r.region,
                path: r.path,
              }}
            />
          </div>
        </div>
      )}
    />
  );
}

// ---------------- Devices & Sessions ----------------
function DevicesTab({ id }: { id: string }) {
  const devices = useUserDevices(id);
  const sessions = useUserSessions(id);
  const accounts = useUserAccounts(id);
  const notifications = useUserNotifications(id, { limit: 20 });
  const qc = useQueryClient();

  const revokeSession = async (sid: string) => {
    try {
      const res = await authClient.admin.revokeUserSession({ sessionToken: sid });
      if ("error" in res && res.error) throw new Error(res.error.message ?? "Failed");
      toast.success("Session revoked");
      qc.invalidateQueries({ queryKey: ["user", id, "sessions"] });
    } catch (err) {
      toast.error("Revoke failed", { description: err instanceof Error ? err.message : "Unknown error" });
    }
  };

  const deviceRows = paginatedItems<DeviceRow>(devices.data);
  const allSessionRows = paginatedItems<SessionRow>(sessions.data);
  const sessionRows = allSessionRows.filter((s) => s.active);
  const expiredSessionCount = allSessionRows.length - sessionRows.length;
  const accountRows = paginatedItems(accounts.data);
  const notifRows = paginatedItems<NotificationRow>(notifications.data);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Devices ({deviceRows.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {devices.isLoading ? (
            <LoadingState />
          ) : deviceRows.length === 0 ? (
            <EmptyState title="No devices registered" />
          ) : (
            <ul className="divide-y divide-border/40 text-sm">
              {deviceRows.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="info" className="capitalize">{d.platform ?? "ios"}</Badge>
                      <Badge variant={d.environment === "sandbox" ? "warn" : "outline"} className="font-mono text-[10px]">
                        {d.environment ?? "production"}
                      </Badge>
                    </div>
                    <div className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">{d.token}</div>
                  </div>
                  <span className="text-xs text-muted-foreground" title="Last registered">
                    <RelTime value={d.updated_at ?? d.created_at} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Active sessions ({sessionRows.length})
            {expiredSessionCount > 0 && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">{expiredSessionCount} expired hidden</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sessions.isLoading ? (
            <LoadingState />
          ) : sessionRows.length === 0 ? (
            <EmptyState title="No active sessions" />
          ) : (
            <ul className="divide-y divide-border/40">
              {sessionRows.map((s) => (
                <li key={s.id} className="flex items-start justify-between gap-3 py-2.5">
                  <div className="min-w-0 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px] text-muted-foreground">{s.id}</span>
                      {s.impersonatedBy && <Badge variant="warn" className="text-[10px]">impersonation</Badge>}
                    </div>
                    <div className="mt-0.5">{s.ipAddress ?? "—"}</div>
                    <div className="mt-0.5 truncate text-muted-foreground">{truncate(s.userAgent ?? "", 80)}</div>
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      Created <RelTime value={s.createdAt} /> · Expires <RelTime value={s.expiresAt} />
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => revokeSession(s.token ?? s.id)}
                  >
                    Revoke
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Linked accounts ({accountRows.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {accounts.isLoading ? (
            <LoadingState />
          ) : accountRows.length === 0 ? (
            <EmptyState title="No linked accounts" />
          ) : (
            <ul className="divide-y divide-border/40 text-sm">
              {accountRows.map((a) => (
                <li key={a.id} className="flex items-center justify-between gap-3 py-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="capitalize">{a.providerId}</Badge>
                    <span className="font-mono text-[11px] text-muted-foreground">{a.accountId}</span>
                  </div>
                  <span className="text-xs text-muted-foreground"><RelTime value={a.createdAt} /></span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent push (last 20)</CardTitle>
        </CardHeader>
        <CardContent>
          {notifications.isLoading ? (
            <LoadingState />
          ) : notifRows.length === 0 ? (
            <EmptyState title="No notifications" />
          ) : (
            <ul className="divide-y divide-border/40 text-sm">
              {notifRows.map((n) => (
                <li key={n.id} className="py-2">
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant="outline" className="font-mono text-[10px]">{n.notification_type}</Badge>
                    <span className="text-[11px] text-muted-foreground"><RelTime value={n.sent_at} /></span>
                  </div>
                  <div className="mt-1 text-sm font-medium">{n.title ?? "—"}</div>
                  <div className="text-xs text-muted-foreground">{truncate(n.body ?? "", 100)}</div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------- Settings/Data ----------------
function DataTab({ id, config }: { id: string; config: Record<string, unknown> | null }) {
  const recurring = useUserRecurring(id);
  const income = useUserIncome(id);
  const budgets = useUserBudgets(id);
  const goals = useUserGoals(id);
  const trips = useUserTrips(id);
  const savings = useUserSavings(id);
  const corrections = useUserCorrections(id);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>user_config</CardTitle>
        </CardHeader>
        <CardContent>
          {config ? <JsonViewer value={config} /> : <EmptyState title="No config row yet" />}
        </CardContent>
      </Card>

      <SmallTable
        title="Recurring"
        loading={recurring.isLoading}
        error={recurring.isError ? recurring.error : undefined}
        onRetry={() => recurring.refetch()}
        rows={paginatedItems<RecurringRow>(recurring.data)}
        cols={[
          { key: "note", header: "Note", cell: (r) => r.note || "—" },
          { key: "type", header: "Type", cell: (r) => <Badge variant="outline">{r.type}</Badge> },
          { key: "category", header: "Category", cell: (r) => r.category || "—" },
          { key: "amount", header: "Amount", cell: (r) => <>{fmtCurrency(r.amount, r.currency)}{r.is_estimated === 1 ? " (est.)" : ""}</> },
          { key: "freq", header: "Frequency", cell: (r) => r.frequency },
          { key: "last", header: "Last logged", cell: (r) => r.last_created_date ?? "—" },
          { key: "active", header: "Active", cell: (r) => (r.is_active === 1 ? <Badge variant="success">yes</Badge> : <Badge variant="neutral">no</Badge>) },
        ]}
        rowKey={(r) => r.id}
      />

      <SmallTable
        title="Income sources"
        loading={income.isLoading}
        error={income.isError ? income.error : undefined}
        onRetry={() => income.refetch()}
        rows={paginatedItems<IncomeRow>(income.data)}
        cols={[
          { key: "name", header: "Name", cell: (r) => r.name || "—" },
          { key: "amount", header: "Amount", cell: (r) => fmtCurrency(r.amount, r.currency) },
          { key: "freq", header: "Frequency", cell: (r) => r.frequency },
          { key: "active", header: "Active", cell: (r) => (r.is_active === 1 ? <Badge variant="success">yes</Badge> : <Badge variant="neutral">no</Badge>) },
        ]}
        rowKey={(r) => r.id}
      />

      <SmallTable
        title="Budgets"
        loading={budgets.isLoading}
        error={budgets.isError ? budgets.error : undefined}
        onRetry={() => budgets.refetch()}
        rows={paginatedItems<BudgetRow>(budgets.data)}
        cols={[
          { key: "category", header: "Category", cell: (r) => r.category ?? "—" },
          { key: "amount", header: "Amount", cell: (r) => fmtCurrency(r.amount, r.currency) },
          { key: "period", header: "Period", cell: (r) => r.period ?? "—" },
        ]}
        rowKey={(r) => r.id}
      />

      <SmallTable
        title="Goals"
        loading={goals.isLoading}
        error={goals.isError ? goals.error : undefined}
        onRetry={() => goals.refetch()}
        rows={paginatedItems<GoalRow>(goals.data)}
        cols={[
          { key: "type", header: "Type", cell: (r) => r.goal_type },
          { key: "description", header: "Description", cell: (r) => r.description ?? "—" },
          // fmtCurrency's default currency only applies to undefined, not null.
          { key: "target", header: "Target", cell: (r) => (r.currency ? fmtCurrency(r.target_amount, r.currency) : r.target_amount ?? "—") },
        ]}
        rowKey={(r) => r.id}
      />

      <Card>
        <CardHeader>
          <CardTitle>Trips</CardTitle>
        </CardHeader>
        <CardContent>
          {trips.isLoading ? (
            <LoadingState />
          ) : trips.isError ? (
            <ErrorState error={trips.error} onRetry={() => trips.refetch()} />
          ) : paginatedItems<TripRow>(trips.data).length === 0 ? (
            <EmptyState title="No trips" />
          ) : (
            <ul className="space-y-3">
              {paginatedItems<TripRow>(trips.data).map((t) => (
                <li key={t.id} className="rounded-md border border-border/40 bg-background/30 p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">
                      {t.country_name} <span className="font-mono text-xs text-muted-foreground">{t.currency}</span>
                    </span>
                    <Badge variant={t.is_active === 1 ? "success" : "outline"}>{t.is_active === 1 ? "active" : "ended"}</Badge>
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {t.start_date} → {t.end_date ?? (t.planned_end_date ? `${t.planned_end_date} (planned)` : "—")}
                    {t.budget_amount != null && t.budget_currency
                      ? ` · holiday budget ${fmtCurrency(t.budget_amount, t.budget_currency)}`
                      : ""}
                  </div>
                  {t.phases && t.phases.length > 0 && (
                    <ul className="mt-2 space-y-0.5 border-l border-border/40 pl-3 text-xs text-muted-foreground">
                      {t.phases.map((p) => (
                        <li key={p.id}>
                          <span className="text-foreground/80">{p.country_name}</span> · {p.start_date} → {p.end_date ?? "—"}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <SmallTable
        title="Savings pots"
        loading={savings.isLoading}
        error={savings.isError ? savings.error : undefined}
        onRetry={() => savings.refetch()}
        rows={paginatedItems<SavingsRow>(savings.data)}
        cols={[
          {
            key: "name",
            header: "Name",
            cell: (r) => (
              <span className="inline-flex items-center gap-1.5">
                {r.emoji} {r.name}
                {r.archived_at && <Badge variant="neutral" className="text-[10px]">archived</Badge>}
              </span>
            ),
          },
          { key: "current", header: "Saved", cell: (r) => fmtCurrency(r.contributed_total, r.currency) },
          { key: "target", header: "Target", cell: (r) => fmtCurrency(r.target_amount, r.currency) },
          { key: "contributions", header: "Contributions", cell: (r) => fmtNumber(r.contributions_count) },
        ]}
        rowKey={(r) => r.id}
      />

      <SmallTable
        title="Corrections"
        loading={corrections.isLoading}
        error={corrections.isError ? corrections.error : undefined}
        onRetry={() => corrections.refetch()}
        rows={paginatedItems<CorrectionRow>(corrections.data)}
        cols={[
          { key: "time", header: "Time", cell: (r) => <span className="whitespace-nowrap text-xs text-muted-foreground"><RelTime value={r.created_at} /></span> },
          { key: "id", header: "ID", cell: (r) => <span className="font-mono text-xs text-muted-foreground">#{r.id}</span> },
          { key: "expense", header: "Expense", cell: (r) => <span className="font-mono text-xs">#{r.expense_id}</span> },
          { key: "correction", header: "Correction", cell: (r) => <CorrectionText text={r.correction_text} /> },
          { key: "before", header: "Before", cell: (r) => <CorrectionBefore previous={r.previous} /> },
        ]}
        rowKey={(r) => r.id}
      />
    </div>
  );
}

// request_log keeps 7 days, so the API caps the streak at 7.
function fmtStreak(days: number): string {
  return days >= 7 ? "7d+" : `${days}d`;
}

interface CorrectionChange {
  field: string;
  from: string;
  to: string;
}

function isCorrectionChange(value: unknown): value is CorrectionChange {
  if (!value || typeof value !== "object") return false;
  const { field, from, to } = value as Record<string, unknown>;
  return typeof field === "string" && typeof from === "string" && typeof to === "string";
}

// Edits made in the app are stored as JSON, chat corrections as the user's message. As in the iOS
// app, only an inline or bulk record with changes counts as an edit; anything else is chat text.
function parseCorrectionEdit(text: string) {
  const trimmed = text.trim();
  if (!trimmed.startsWith("{")) return null;
  try {
    const { source, changes, undo_of } = JSON.parse(trimmed) as Record<string, unknown>;
    if (source !== "inline" && source !== "bulk") return null;
    if (!Array.isArray(changes) || changes.length === 0 || !changes.every(isCorrectionChange)) return null;
    return { source, changes, undoOf: typeof undo_of === "number" ? undo_of : null };
  } catch {
    return null;
  }
}

const SOURCE_BADGE = "font-mono text-[10px] uppercase";

function CorrectionText({ text }: { text: string }) {
  const edit = parseCorrectionEdit(text);
  if (!edit) {
    return (
      <div className="space-y-1">
        <Badge variant="neutral" className={SOURCE_BADGE}>chat</Badge>
        <div className="whitespace-pre-wrap text-xs">{text.trim() || <span className="text-muted-foreground">—</span>}</div>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1">
        <Badge variant="neutral" className={SOURCE_BADGE}>{edit.source}</Badge>
        {edit.undoOf != null && <Badge variant="warn" className={SOURCE_BADGE}>undo of #{edit.undoOf}</Badge>}
      </div>
      <ul className="space-y-0.5 font-mono text-xs">
        {edit.changes.map((change, i) => (
          <li key={i}>
            {change.field}: <span className="text-rose-300">{change.from || "—"}</span>
            <span className="text-muted-foreground"> → </span>
            <span className="text-emerald-300">{change.to || "—"}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CorrectionBefore({ previous }: { previous: CorrectionPrevious | null }) {
  if (!previous) return <span className="text-muted-foreground">—</span>;
  const entered =
    previous.original_amount != null && previous.original_currency && previous.original_currency !== previous.currency
      ? fmtCurrency(previous.original_amount, previous.original_currency)
      : null;
  // Entries paid from a pot or holiday budget are left out of the budget, so name what paid.
  const paidFrom =
    previous.funding_source === "budget"
      ? null
      : [previous.funding_emoji, previous.funding_name ?? previous.funding_source].filter(Boolean).join(" ");
  return (
    <div className="space-y-0.5 text-xs">
      <div className="tabular-nums">
        {fmtCurrency(previous.amount, previous.currency)}
        {entered && <span className="text-muted-foreground"> ({entered})</span>}
      </div>
      <div className="text-muted-foreground">
        {[previous.expense_date, previous.category, truncate(previous.note, 60)].filter(Boolean).join(" · ")}
      </div>
      {paidFrom && <div className="text-muted-foreground">paid from {paidFrom}</div>}
    </div>
  );
}

function SmallTable<T>({
  title,
  loading,
  error,
  onRetry,
  rows,
  cols,
  rowKey,
}: {
  title: string;
  loading: boolean;
  error?: unknown;
  onRetry?: () => void;
  rows: T[];
  cols: Column<T>[];
  rowKey: (row: T) => string | number;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {title} <span className="ml-2 text-xs font-normal text-muted-foreground">({rows.length})</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <DataTable columns={cols} rows={rows} loading={loading} error={error} onRetry={onRetry} rowKey={rowKey} dense />
      </CardContent>
    </Card>
  );
}

// ---------------- Danger ----------------
function DangerTab({ user }: { user: import("@/types").AdminUser }) {
  return (
    <Card className="border-rose-500/30 bg-rose-950/10">
      <CardHeader>
        <CardTitle className="text-rose-200">Danger zone</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-4 text-sm text-muted-foreground">
          These actions are irreversible. Reset data, reset onboarding, delete and send push are recorded in the
          audit log; ban, unban and set password go through better-auth and are not.
        </p>
        <p className="mb-4 text-xs text-muted-foreground">
          User role can only be changed in SQL: <code className="font-mono">UPDATE "user" SET role = 'admin' WHERE email = …</code>
        </p>
        <UserActions user={user} variant="buttons" />
      </CardContent>
    </Card>
  );
}
